import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/api';

const AuthTestPage = () => {
  const { user, isAuthenticated, login, logout } = useAuth();
  const [testCredentials, setTestCredentials] = useState({
    email: 'admin@ncrs.gov.ph',
    password: 'ncrs@admin2025'
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTestLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await login(testCredentials);
      console.log('Login successful!');
    } catch (error) {
      setError('Login failed: ' + error.message);
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const [reportsResponse, statsResponse] = await Promise.all([
        dashboardService.getReports(),
        dashboardService.getStatistics()
      ]);

      setDashboardData({
        reports: reportsResponse.data,
        statistics: statsResponse.data
      });

      console.log('Dashboard data:', { reportsResponse, statsResponse });
    } catch (error) {
      setError('Dashboard request failed: ' + error.message);
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setDashboardData(null);
      console.log('Logout successful!');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Authentication Test Page</h1>

      {/* Authentication Status */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4">Authentication Status</h2>
        <div className="space-y-2">
          <p><strong>Authenticated:</strong> {isAuthenticated() ? '✅ Yes' : '❌ No'}</p>
          {user && (
            <>
              <p><strong>User ID:</strong> {user.id}</p>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Timestamp:</strong> {user.timestamp}</p>
            </>
          )}
        </div>
      </div>

      {/* Login Test */}
      {!isAuthenticated() && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4">Test Login</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email:</label>
              <input
                type="email"
                value={testCredentials.email}
                onChange={(e) => setTestCredentials({...testCredentials, email: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password:</label>
              <input
                type="password"
                value={testCredentials.password}
                onChange={(e) => setTestCredentials({...testCredentials, password: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <button
              onClick={handleTestLogin}
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Test Login'}
            </button>
          </div>
        </div>
      )}

      {/* Dashboard Test */}
      {isAuthenticated() && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4">Test Dashboard Access</h2>
          <div className="space-y-4">
            <button
              onClick={handleTestDashboard}
              disabled={loading}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50 mr-4"
            >
              {loading ? 'Loading...' : 'Test Dashboard API'}
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded mb-6">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Dashboard Data Display */}
      {dashboardData && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Dashboard Data</h2>
          <div className="space-y-4">
            {dashboardData.statistics && (
              <div>
                <h3 className="font-semibold mb-2">Statistics:</h3>
                <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
                  {JSON.stringify(dashboardData.statistics, null, 2)}
                </pre>
              </div>
            )}
            {dashboardData.reports && (
              <div>
                <h3 className="font-semibold mb-2">Reports:</h3>
                <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96">
                  {JSON.stringify(dashboardData.reports, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthTestPage;
